import { useState, useRef } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Camera,
  Upload,
  Search,
  Loader2,
  Image as ImageIcon,
  FileText,
  X,
  AlertCircle,
  CheckCircle2,
} from 'lucide-react';
import { documentsService } from '@/services/api/documents';

interface ImageSearchResult {
  image: {
    imageId: string;
    documentId: string;
    pageNumber: number;
    s3Key: string;
    caption?: string;
    similarity: number;
  };
  textChunks: Array<{
    chunkId: string;
    text: string;
    similarity: number;
  }>;
}

interface SearchResponse {
  success: boolean;
  query: {
    type: 'image' | 'text';
    analysis?: {
      caption?: string;
      imageType?: string;
      ocrText?: string;
    };
  };
  results: ImageSearchResult[];
}

export function ImageSearchPage() {
  const [searchMode, setSearchMode] = useState<'photo' | 'text'>('photo');
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [textQuery, setTextQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResponse | null>(null);
  const [resultImageUrls, setResultImageUrls] = useState<Record<string, string>>({});
  const fileInputRef = useRef<HTMLInputElement>(null);

  const searchMutation = useMutation({
    mutationFn: async (params: { image?: string; text?: string; contentType?: string }) => {
      const response = await documentsService.queryImageSimilarity({
        image: params.image,
        text: params.text,
        contentType: params.contentType,
        limit: 10,
      });
      return response as SearchResponse;
    },
    onSuccess: async (data) => {
      setSearchResults(data);

      // Load presigned URLs for result images
      const urls: Record<string, string> = {};
      for (const result of data.results) {
        if (result.image.s3Key) {
          try {
            const url = await documentsService.getImageUrl(
              result.image.documentId,
              result.image.s3Key
            );
            urls[result.image.imageId] = url;
          } catch (error) {
            console.error('Failed to get image URL:', error);
          }
        }
      }
      setResultImageUrls(urls);
    },
  });

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    // Read file as base64
    const reader = new FileReader();
    reader.onload = (event) => {
      const base64 = event.target?.result as string;
      // Remove data URL prefix to get just the base64 data
      const base64Data = base64.split(',')[1];
      setSelectedImage(base64Data);
      setImagePreview(base64);
    };
    reader.readAsDataURL(file);
  };

  const handleSearch = () => {
    if (searchMode === 'photo' && selectedImage) {
      searchMutation.mutate({
        image: selectedImage,
        contentType: 'image/jpeg',
      });
    } else if (searchMode === 'text' && textQuery.trim()) {
      searchMutation.mutate({
        text: textQuery.trim(),
      });
    }
  };

  const clearImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const clearResults = () => {
    setSearchResults(null);
    setResultImageUrls({});
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary text-primary-foreground">
          <Search className="h-5 w-5" />
        </div>
        <div>
          <h1 className="text-2xl font-bold">Visual Image Search</h1>
          <p className="text-sm text-muted-foreground">
            Search your documents by uploading a photo or describing what you're looking for
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Search Panel */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Search Method</CardTitle>
            <CardDescription>
              Upload a photo to find similar images, or describe what you're looking for
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs value={searchMode} onValueChange={(v) => setSearchMode(v as 'photo' | 'text')}>
              <TabsList className="grid w-full grid-cols-2 mb-4">
                <TabsTrigger value="photo" className="flex items-center gap-2">
                  <Camera className="h-4 w-4" />
                  Photo Search
                </TabsTrigger>
                <TabsTrigger value="text" className="flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Text Search
                </TabsTrigger>
              </TabsList>

              <TabsContent value="photo" className="space-y-4">
                <div
                  className="border-2 border-dashed rounded-lg p-6 text-center cursor-pointer hover:border-primary/50 transition-colors"
                  onClick={() => fileInputRef.current?.click()}
                >
                  {imagePreview ? (
                    <div className="relative inline-block">
                      <img
                        src={imagePreview}
                        alt="Selected"
                        className="max-h-48 max-w-full rounded-lg shadow-md"
                      />
                      <Button
                        variant="destructive"
                        size="icon"
                        className="absolute -top-2 -right-2 h-6 w-6"
                        onClick={(e) => {
                          e.stopPropagation();
                          clearImage();
                        }}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  ) : (
                    <>
                      <Upload className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
                      <p className="text-sm font-medium">Click to upload a photo</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Take a photo of equipment, diagrams, or any item to find matches
                      </p>
                    </>
                  )}
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  className="hidden"
                  onChange={handleFileSelect}
                />
                <Button
                  className="w-full"
                  disabled={!selectedImage || searchMutation.isPending}
                  onClick={handleSearch}
                >
                  {searchMutation.isPending ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Searching...
                    </>
                  ) : (
                    <>
                      <Search className="h-4 w-4 mr-2" />
                      Search by Photo
                    </>
                  )}
                </Button>
              </TabsContent>

              <TabsContent value="text" className="space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Describe what you're looking for</label>
                  <Input
                    value={textQuery}
                    onChange={(e) => setTextQuery(e.target.value)}
                    placeholder="e.g., circuit diagram, safety equipment, flow chart..."
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  />
                  <p className="text-xs text-muted-foreground">
                    Search for charts, diagrams, photos, or any visual content in your documents
                  </p>
                </div>
                <Button
                  className="w-full"
                  disabled={!textQuery.trim() || searchMutation.isPending}
                  onClick={handleSearch}
                >
                  {searchMutation.isPending ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Searching...
                    </>
                  ) : (
                    <>
                      <Search className="h-4 w-4 mr-2" />
                      Search by Description
                    </>
                  )}
                </Button>
              </TabsContent>
            </Tabs>

            {/* Query Analysis */}
            {searchResults?.query.analysis && (
              <div className="mt-4 p-3 bg-muted rounded-lg">
                <p className="text-xs font-medium mb-1">AI Analysis of Your Photo:</p>
                <p className="text-sm text-muted-foreground">
                  {searchResults.query.analysis.caption}
                </p>
                {searchResults.query.analysis.imageType && (
                  <Badge variant="secondary" className="mt-2 text-xs">
                    Type: {searchResults.query.analysis.imageType}
                  </Badge>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Results Panel */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-lg">Search Results</CardTitle>
              <CardDescription>
                {searchResults
                  ? `Found ${searchResults.results.length} matching images`
                  : 'Upload a photo or enter a description to search'}
              </CardDescription>
            </div>
            {searchResults && (
              <Button variant="ghost" size="sm" onClick={clearResults}>
                <X className="h-4 w-4 mr-1" />
                Clear
              </Button>
            )}
          </CardHeader>
          <CardContent>
            {searchMutation.isPending ? (
              <div className="flex flex-col items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary mb-3" />
                <p className="text-sm text-muted-foreground">
                  Searching your knowledge base...
                </p>
              </div>
            ) : searchMutation.isError ? (
              <div className="flex flex-col items-center justify-center py-12 text-destructive">
                <AlertCircle className="h-8 w-8 mb-3" />
                <p className="text-sm">Search failed. Please try again.</p>
              </div>
            ) : searchResults?.results.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                <ImageIcon className="h-8 w-8 mb-3" />
                <p className="text-sm">No matching images found</p>
                <p className="text-xs mt-1">Try a different photo or description</p>
              </div>
            ) : searchResults ? (
              <div className="space-y-4 max-h-[500px] overflow-y-auto">
                {searchResults.results.map((result, index) => (
                  <div
                    key={result.image.imageId}
                    className="border rounded-lg overflow-hidden hover:border-primary/50 transition-colors"
                  >
                    {/* Image */}
                    <div className="bg-muted/30 p-2 flex items-center justify-center min-h-[120px]">
                      {resultImageUrls[result.image.imageId] ? (
                        <img
                          src={resultImageUrls[result.image.imageId]}
                          alt={result.image.caption || 'Search result'}
                          className="max-h-40 max-w-full rounded border shadow-sm"
                        />
                      ) : (
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          <span className="text-xs">Loading image...</span>
                        </div>
                      )}
                    </div>

                    {/* Details */}
                    <div className="p-3 space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Badge variant="default" className="text-xs">
                            #{index + 1}
                          </Badge>
                          <Badge variant="outline" className="text-xs">
                            Page {result.image.pageNumber}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-1">
                          <CheckCircle2 className="h-3 w-3 text-green-500" />
                          <span className="text-xs font-medium text-green-600">
                            {(result.image.similarity * 100).toFixed(0)}% match
                          </span>
                        </div>
                      </div>

                      {result.image.caption && (
                        <p className="text-sm text-muted-foreground line-clamp-2">
                          {result.image.caption}
                        </p>
                      )}

                      {/* Related Text */}
                      {result.textChunks.length > 0 && (
                        <div className="mt-2 pt-2 border-t">
                          <p className="text-xs font-medium mb-1">Related Content:</p>
                          <p className="text-xs text-muted-foreground line-clamp-3">
                            {result.textChunks[0].text}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                <Camera className="h-12 w-12 mb-3 opacity-50" />
                <p className="text-sm font-medium">Ready to Search</p>
                <p className="text-xs mt-1 text-center max-w-xs">
                  Take a photo of equipment, a diagram, or any item to find matching images in your documents
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Tips */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Tips for Better Search Results</CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-muted-foreground">
            <li className="flex items-start gap-2">
              <Camera className="h-4 w-4 mt-0.5 text-primary" />
              <span>Take clear, well-lit photos of the item you want to find</span>
            </li>
            <li className="flex items-start gap-2">
              <ImageIcon className="h-4 w-4 mt-0.5 text-primary" />
              <span>Ensure the main subject is centered in the frame</span>
            </li>
            <li className="flex items-start gap-2">
              <FileText className="h-4 w-4 mt-0.5 text-primary" />
              <span>For text search, be specific about what you're looking for</span>
            </li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}

export default ImageSearchPage;
